import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { asChakraVariable } from '@mangovoice/design-system'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

import { siteNavigationFocusRing } from './SiteNavigationTab.style'

export enum SiteNavigationCollapseTabSizes {
  Normal = 'normal',
  Collapsed = 'collapsed',
}

const SiteNavigationCollapseTabParts = [
  'container',
  'contentWrapper',
  'arrowIconWrapper',
  'arrowIcon',
  'settingsIconWrapper',
  'settingsIcon',
  'text',
] as const

const helpers = createMultiStyleConfigHelpers(SiteNavigationCollapseTabParts)

export const SiteNavigationCollapseTabStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
    },
    contentWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      height: '1.75rem',
      px: '0.75rem',
      flex: 1,
      cursor: 'pointer',
      borderRadius: '2.5rem',
      _hover: {
        bg: 'bg.emphasized',
      },
      _active: {
        bg: 'bg.muted',
      },
      _focusVisible: siteNavigationFocusRing,
    },
    arrowIcon: {
      color: 'fg.muted',
    },
    settingsIconWrapper: {
      height: '2rem',
      width: '2rem',
      display: 'flex',
      direction: 'column',
      justifyContent: 'center',
      borderRadius: '0.5rem',
      alignItems: 'center',
      flexShrink: '0',
      cursor: 'pointer',
      _hover: {
        bg: 'bg.emphasized',
        '> .collapseTabSettingsIcon': {
          color: 'fg.subtle',
        },
      },
      _active: {
        bg: 'bg.muted',
        '> .collapseTabSettingsIcon': {
          color: 'fg.default',
        },
      },
      _selected: {
        bg: 'bg.emphasized',
        boxShadow: `0px 1px 0px 0px ${asChakraVariable({ default: 'bg.muted' })}`,
      },
      _focusVisible: siteNavigationFocusRing,
    },
    settingsIcon: {
      boxSize: '1.375rem',
      color: 'fg.muted',
    },
    text: {
      ...textStyles[textStyleKeys.Body2],
      color: 'fg.muted',
      '.collapse-tab-content:hover &': {
        color: 'fg.default',
      },
      '.collapse-tab-content:active &': {
        color: 'fg.default',
      },
    },
  },
  sizes: {
    [SiteNavigationCollapseTabSizes.Normal]: {
      container: {
        width: '10.75rem',
        height: '3rem',
        py: '0.5rem',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
        borderRadius: '0.5rem',
        userSelect: 'none',
      },
      arrowIconWrapper: {
        boxSize: '1.5rem',
        direction: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      },
      arrowIcon: {
        boxSize: '.75rem',
        '.collapse-tab-content:hover &': {
          color: 'fg.default',
        },
        '.collapse-tab-content:active &': {
          color: 'fg.default',
        },
      },
    },
    [SiteNavigationCollapseTabSizes.Collapsed]: {
      container: {
        display: 'flex',
        padding: '0.75rem',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '0.75rem',
        alignSelf: 'stretch',
      },
      arrowIconWrapper: {
        boxSize: '1.75rem',
        direction: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        borderRadius: '2.5rem',
        _hover: {
          bg: 'bg.emphasized',
          '> .collapse-tab-arrow-icon': {
            color: 'fg.subtle',
          },
        },
        _active: {
          bg: 'bg.muted',
          '> .collapse-tab-arrow-icon': {
            color: 'fg.default',
          },
        },
        _focusVisible: siteNavigationFocusRing,
      },
      arrowIcon: {
        boxSize: '1rem',
      },
    },
  },
  defaultProps: {
    size: SiteNavigationCollapseTabSizes.Normal,
  },
})

export const useSiteNavigationCollapseTabStyle = (
  props?: StyleThemingProps,
) => {
  return mergeMultiStyleConfig(
    SiteNavigationCollapseTabStyle,
    props,
  ) as MultiStyleType<typeof SiteNavigationCollapseTabParts>
}
